import { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 32px;
  text-align: center;
  background: ${({ theme }) => theme?.colors?.background ?? '#0a0a0f'};
  color: ${({ theme }) => theme?.colors?.text ?? '#f5f5f5'};
`;

const Emoji = styled.div`
  font-size: 3rem;
  line-height: 1;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
`;

const Message = styled.p`
  max-width: 460px;
  font-size: 0.95rem;
  opacity: 0.75;
  line-height: 1.5;
  margin: 0;
`;

const Detail = styled.pre`
  max-width: 560px;
  max-height: 160px;
  overflow: auto;
  padding: 12px 16px;
  border-radius: 8px;
  background: rgba(127, 127, 127, 0.12);
  font-size: 0.75rem;
  text-align: left;
  white-space: pre-wrap;
  word-break: break-word;
`;

const ReloadButton = styled.button`
  padding: 12px 28px;
  border: none;
  border-radius: 10px;
  background: #8b5cf6;
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.9;
  }
`;

/**
 * Error boundary del árbol autenticado. Captura errores de render de cualquier
 * descendiente y evita la pantalla en blanco — ofrece recargar. Debe ser class
 * component (los hooks no exponen getDerivedStateFromError / componentDidCatch).
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log para diagnóstico; en prod se ve en la consola del browser.
    console.error('ErrorBoundary atrapó un error:', error, info.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Wrapper role="alert">
          <Emoji aria-hidden>💥</Emoji>
          <Title>Something went wrong</Title>
          <Message>
            An unexpected error occurred. Reloading usually fixes it. If it keeps
            happening, let us know.
          </Message>
          {this.state.error?.message && <Detail>{this.state.error.message}</Detail>}
          <ReloadButton type="button" onClick={this.handleReload}>
            Reload the page
          </ReloadButton>
        </Wrapper>
      );
    }

    return this.props.children;
  }
}
